import React, { Component } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Button,
  Container,
  Badge,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  CustomInput,
} from "reactstrap";
import classnames from "classnames";
import {
  getTransportServicesAdmin,
  updateTransportService,
} from "../../Endpoint/Admin/index";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import { Link } from "react-router-dom";

class TransportServices extends Component {
  constructor(props) {
    super(props);
    this.state = {
      services: [],
      isLoaded: false,
      showEditModal: false,
      editingService: null,
      activeTab: "protocol",
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Transport", link: "/transport/requests" },
        { title: "Services", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadServices();
  }

  loadServices = () => {
    getTransportServicesAdmin()
      .then((result) => {
        if (result && result.status === 200) {
          this.setState({ services: result.data, isLoaded: true });
        } else {
          toast.error(result?.message || "Failed to load services");
        }
      })
      .catch(() => toast.error("Error loading services"));
  };

  handleEdit = (service) => {
    // Use dynamic tierPricing array if available, otherwise fallback to legacy
    let tierPricing = service.tierPricing || [];

    // If no tierPricing but has availableTiers, initialize from legacy pricing
    if (tierPricing.length === 0 && service.availableTiers) {
      tierPricing = service.availableTiers.map((tier) => {
        const legacyDayPrice = service.tieredPricing?.[tier.code.toLowerCase()] || 0;
        const legacyNightPrice = service.nightPricing?.[tier.code.toLowerCase()] || 0;
        return {
          tierCode: tier.code,
          tierId: tier._id,
          tierName: tier.name,
          tierColor: tier.color,
          tierIcon: tier.icon,
          displayOrder: tier.displayOrder,
          dayPrice: legacyDayPrice,
          nightPrice: legacyNightPrice,
        };
      });
    } else if (tierPricing.length > 0) {
      // Preserve existing nightPrice values
      tierPricing = tierPricing.map((tier) => ({
        ...tier,
        nightPrice: tier.nightPrice || 0,
      }));
    }

    // Keep legacy fields for backward compatibility
    const tieredPricing = service.tieredPricing || { standard: 0, comfort: 0, vip: 0 };
    const nightPricing = service.nightPricing || {
      standard: 0,
      comfort: 0,
      vip: 0,
    };

    this.setState({
      editingService: {
        ...service,
        tierPricing, // Dynamic tier pricing array
        discount: service.discount || {
          isEnabled: false,
          type: "percentage",
          value: 0,
          displayLabel: "",
        },
        tieredPricing,
        nightPricing,
        hourlyPricing: service.hourlyPricing || {
          ratePerHour: 0,
          minimumHours: 4,
          standardRate: 0,
          comfortRate: 0,
          vipRate: 0,
        },
        outstationRates: service.outstationRates || {
          dayRateMultiplier: 1.0,
          nightRateMultiplier: 1.5,
          nightStartHour: 20,
          nightEndHour: 6,
          driverAllowancePerNight: 50,
        },
      },
      showEditModal: true,
    });
  };

  handleSave = () => {
    const { editingService } = this.state;

    const updateData = {
      basePrice: editingService.basePrice,
      description: editingService.description,
      isActive: editingService.isActive,
      estimatedDuration: editingService.estimatedDuration,
      maxPassengers: editingService.maxPassengers,
      maxLuggage: editingService.maxLuggage,
      minimumKm: editingService.minimumKm,
      minimumCharge: editingService.minimumCharge,
      discount: editingService.discount,
    };

    // Include tiered pricing for tiered services (protocol, transport, combo)
    if (editingService.pricingMode === "tiered") {
      // Use dynamic tierPricing array
      updateData.tierPricing = editingService.tierPricing;

      // Also update legacy fields for backward compatibility
      const legacyTiered = {};
      const legacyNight = {};
      (editingService.tierPricing || []).forEach((tp) => {
        const key = tp.tierCode.toLowerCase();
        legacyTiered[key] = tp.dayPrice || 0;
        legacyNight[key] = tp.nightPrice || 0;
      });
      updateData.tieredPricing = { ...editingService.tieredPricing, ...legacyTiered };
      updateData.nightPricing = { ...editingService.nightPricing, ...legacyNight };
    }

    // Include hourly pricing for day rental
    if (editingService.pricingMode === "hourly") {
      updateData.hourlyPricing = editingService.hourlyPricing;
    }

    // Include outstation rates for outstation services
    if (editingService.pricingMode === "day_night_km") {
      updateData.outstationRates = editingService.outstationRates;
    }

    updateTransportService(editingService._id, updateData)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Service updated successfully");
          this.setState({ showEditModal: false, editingService: null });
          this.loadServices();
        } else {
          toast.error(response?.message || "Failed to update service");
        }
      })
      .catch(() => toast.error("Error updating service"));
  };

  toggleTab = (tab) => {
    if (this.state.activeTab !== tab) {
      this.setState({ activeTab: tab });
    }
  };

  getServiceIcon = (service) => {
    return service.icon || "ri-car-line";
  };

  getServiceColor = (code) => {
    const colors = {
      // New service codes
      PROTOCOL_ARRIVAL: "info",
      PROTOCOL_DEPARTURE: "info",
      TRANSPORT_ARRIVAL: "primary",
      TRANSPORT_DEPARTURE: "primary",
      COMBO_ARRIVAL: "success",
      COMBO_DEPARTURE: "success",
      DAY_RENTAL: "warning",
      OUTSTATION_ONEWAY: "danger",
      OUTSTATION_RETURN: "danger",
      // Legacy codes
      PICKUP: "primary",
      DROP: "info",
      PICK_DROP: "success",
      MEET_GREET: "warning",
      INTERCITY: "success",
      OUTSTATION: "danger",
    };
    return colors[code] || "secondary";
  };

  getPricingModeLabel = (mode) => {
    const labels = {
      fixed: "Fixed Price",
      tiered: "Tiered (Standard/Comfort/VIP)",
      hourly: "Hourly Rate",
      per_km: "Per KM",
      day_night_km: "Day/Night per KM",
    };
    return labels[mode] || mode;
  };

  getCategoryLabel = (category) => {
    const labels = {
      protocol: "Protocol (Meet & Greet)",
      transport: "Transport (Airport)",
      protocol_transport: "Protocol & Transport",
      day_rental: "Day Rental",
      outstation: "Outstation",
      // Legacy
      airport: "Airport Transfer",
      intercity: "Inter-city",
    };
    return labels[category] || category;
  };

  renderPriceWithDiscount = (service) => {
    const { discount, basePrice, pricingMode, tieredPricing, nightPricing, hourlyPricing, tierPricing } = service;

    // Tiered pricing (Protocol, Transport, Combo) - using dynamic tiers
    if (pricingMode === "tiered") {
      // Use dynamic tierPricing array if available
      if (tierPricing && tierPricing.length > 0) {
        return (
          <div>
            <div className="d-flex flex-column">
              {tierPricing.slice(0, 3).map((tier) => (
                <small key={tier.tierCode} className="text-muted">
                  {tier.tierName || tier.tierCode}:{" "}
                  <span className="font-weight-bold" style={{ color: tier.tierColor || '#5664d2' }}>
                    ${tier.dayPrice || 0}
                  </span>
                  {tier.nightPrice > 0 && (
                    <span className="text-secondary">
                      {" "}/ ${tier.nightPrice}
                      <i className="ri-moon-line ml-1" title="Night"></i>
                    </span>
                  )}
                </small>
              ))}
              {tierPricing.length > 3 && (
                <small className="text-muted">+{tierPricing.length - 3} more tiers</small>
              )}
            </div>
            {discount?.isEnabled && discount?.value > 0 && (
              <Badge color="success" pill className="mt-1">
                {discount.displayLabel || `${discount.value}${discount.type === "percentage" ? "%" : "$"} OFF`}
              </Badge>
            )}
          </div>
        );
      }

      // Fallback to legacy tieredPricing
      if (tieredPricing) {
        return (
          <div>
            <div className="d-flex flex-column">
              <small className="text-muted">
                Standard: <span className="text-primary font-weight-bold">${tieredPricing.standard || 0}</span>
                {nightPricing?.standard > 0 && <span className="text-secondary"> / ${nightPricing.standard}<i className="ri-moon-line ml-1" title="Night"></i></span>}
              </small>
              <small className="text-muted">
                Comfort: <span className="text-info font-weight-bold">${tieredPricing.comfort || 0}</span>
                {nightPricing?.comfort > 0 && <span className="text-secondary"> / ${nightPricing.comfort}<i className="ri-moon-line ml-1" title="Night"></i></span>}
              </small>
              <small className="text-muted">
                VIP: <span className="text-warning font-weight-bold">${tieredPricing.vip || 0}</span>
                {nightPricing?.vip > 0 && <span className="text-secondary"> / ${nightPricing.vip}<i className="ri-moon-line ml-1" title="Night"></i></span>}
              </small>
            </div>
            {discount?.isEnabled && discount?.value > 0 && (
              <Badge color="success" pill className="mt-1">
                {discount.displayLabel || `${discount.value}${discount.type === "percentage" ? "%" : "$"} OFF`}
              </Badge>
            )}
          </div>
        );
      }
    }

    // Hourly pricing (Day Rental)
    if (pricingMode === "hourly" && hourlyPricing) {
      return (
        <div>
          <div className="d-flex flex-column">
            <small className="text-muted">Standard: <span className="text-primary font-weight-bold">${hourlyPricing.standardRate || 0}/hr</span></small>
            <small className="text-muted">Comfort: <span className="text-info font-weight-bold">${hourlyPricing.comfortRate || 0}/hr</span></small>
            <small className="text-muted">VIP: <span className="text-warning font-weight-bold">${hourlyPricing.vipRate || 0}/hr</span></small>
          </div>
          <small className="text-muted d-block">Min: {hourlyPricing.minimumHours || 4} hrs</small>
          {discount?.isEnabled && discount?.value > 0 && (
            <Badge color="success" pill className="mt-1">
              {discount.displayLabel || `${discount.value}${discount.type === "percentage" ? "%" : "$"} OFF`}
            </Badge>
          )}
        </div>
      );
    }

    // Per-km pricing (Outstation)
    if (pricingMode === "per_km" || pricingMode === "day_night_km") {
      const minCharge = service.minimumCharge || 0;
      if (discount?.isEnabled && discount?.value > 0) {
        let discountedMin = minCharge;
        if (discount.type === "percentage") {
          discountedMin = minCharge * (1 - discount.value / 100);
        } else {
          discountedMin = minCharge - discount.value;
        }

        return (
          <div>
            <span className="text-muted">Min Charge: </span>
            <span className="text-muted text-decoration-line-through mr-1">${minCharge}</span>
            <span className="text-success font-weight-bold">${discountedMin.toFixed(0)}</span>
            <br />
            <Badge color="success" pill className="mt-1">
              {discount.displayLabel || `${discount.value}${discount.type === "percentage" ? "%" : "$"} OFF`}
            </Badge>
          </div>
        );
      }
      return (
        <div>
          <span className="text-muted">Min Charge: </span>
          <span className="font-weight-bold">${minCharge}</span>
        </div>
      );
    }

    // Fixed price services (legacy)
    if (discount?.isEnabled && discount?.value > 0) {
      let discountedPrice = basePrice;
      if (discount.type === "percentage") {
        discountedPrice = basePrice * (1 - discount.value / 100);
      } else {
        discountedPrice = basePrice - discount.value;
      }

      return (
        <div>
          <span className="text-muted text-decoration-line-through mr-2">${basePrice}</span>
          <span className="text-success font-weight-bold font-size-18">${discountedPrice.toFixed(0)}</span>
          <br />
          <Badge color="success" pill className="mt-1">
            {discount.displayLabel || `${discount.value}${discount.type === "percentage" ? "%" : "$"} OFF`}
          </Badge>
        </div>
      );
    }

    return <span className="text-primary font-weight-bold font-size-18">${basePrice}</span>;
  };

  updateEditingService = (field, value) => {
    this.setState((prevState) => ({
      editingService: {
        ...prevState.editingService,
        [field]: value,
      },
    }));
  };

  updateDiscount = (field, value) => {
    this.setState((prevState) => ({
      editingService: {
        ...prevState.editingService,
        discount: {
          ...prevState.editingService.discount,
          [field]: value,
        },
      },
    }));
  };

  updateOutstationRates = (field, value) => {
    this.setState((prevState) => ({
      editingService: {
        ...prevState.editingService,
        outstationRates: {
          ...prevState.editingService.outstationRates,
          [field]: value,
        },
      },
    }));
  };

  updateTieredPricing = (field, value) => {
    this.setState((prevState) => ({
      editingService: {
        ...prevState.editingService,
        tieredPricing: {
          ...prevState.editingService.tieredPricing,
          [field]: value,
        },
      },
    }));
  };

  // Update dynamic tier pricing (for linked service tiers)
  updateDynamicTierPricing = (tierCode, field, value) => {
    this.setState((prevState) => {
      const tierPricing = [...(prevState.editingService.tierPricing || [])];
      const tierIndex = tierPricing.findIndex((tp) => tp.tierCode === tierCode);

      if (tierIndex >= 0) {
        tierPricing[tierIndex] = {
          ...tierPricing[tierIndex],
          [field]: value,
        };

      }

      return {
        editingService: {
          ...prevState.editingService,
          tierPricing,
        },
      };
    });
  };

  updateHourlyPricing = (field, value) => {
    this.setState((prevState) => ({
      editingService: {
        ...prevState.editingService,
        hourlyPricing: {
          ...prevState.editingService.hourlyPricing,
          [field]: value,
        },
      },
    }));
  };

  renderServiceCard = (service) => {
    return (
      <Col lg={6} key={service._id}>
        <Card className={`mb-4 ${!service.isActive ? "bg-light" : ""}`}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-start">
              <div className="d-flex align-items-center">
                <div
                  className={`avatar-sm rounded-circle bg-soft-${this.getServiceColor(service.serviceCode)} mr-3`}
                  style={{
                    width: "48px",
                    height: "48px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className={`${this.getServiceIcon(service)} font-size-24 text-${this.getServiceColor(service.serviceCode)}`}></i>
                </div>
                <div>
                  <h5 className="mb-1">{service.serviceName}</h5>
                  <div>
                    <Badge color={service.isActive ? "success" : "secondary"} className="mr-2">
                      {service.isActive ? "Active" : "Inactive"}
                    </Badge>
                    <Badge color="light" className="text-muted">
                      {this.getPricingModeLabel(service.pricingMode)}
                    </Badge>
                  </div>
                </div>
              </div>
              <Button color="light" size="sm" onClick={() => this.handleEdit(service)}>
                <i className="ri-edit-line"></i> Edit
              </Button>
            </div>

            <p className="text-muted mt-3 mb-3">{service.description}</p>

            <Row className="text-center border-top pt-3">
              <Col xs={3}>
                <div className="mb-1">{this.renderPriceWithDiscount(service)}</div>
                <small className="text-muted">
                  {service.pricingMode === "fixed" ? "Base Price" : "Min Charge"}
                </small>
              </Col>
              <Col xs={3}>
                <h4 className="mb-1">{service.estimatedDuration || "-"}</h4>
                <small className="text-muted">Minutes</small>
              </Col>
              <Col xs={3}>
                <h4 className="mb-1">{service.maxPassengers || "-"}</h4>
                <small className="text-muted">Guests</small>
              </Col>
              <Col xs={3}>
                {service.pricingMode !== "fixed" ? (
                  <>
                    <h4 className="mb-1">{service.minimumKm || 0}</h4>
                    <small className="text-muted">Min KM</small>
                  </>
                ) : (
                  <>
                    <h4 className="mb-1">{service.maxLuggage || "-"}</h4>
                    <small className="text-muted">Luggage</small>
                  </>
                )}
              </Col>
            </Row>

            {service.freeWithServices && service.freeWithServices.length > 0 && (
              <div className="mt-3 pt-3 border-top">
                <small className="text-success">
                  <i className="ri-gift-line mr-1"></i>
                  Free when bundled with: {service.freeWithServices.join(", ")}
                </small>
              </div>
            )}

            {service.pricingMode === "day_night_km" && service.outstationRates && (
              <div className="mt-3 pt-3 border-top">
                <small className="text-info">
                  <i className="ri-moon-line mr-1"></i>
                  Night rate: {service.outstationRates.nightRateMultiplier}x |
                  Driver allowance: ${service.outstationRates.driverAllowancePerNight}/night
                </small>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    );
  };

  render() {
    const { services, showEditModal, editingService, activeTab } = this.state;

    // Group services by new categories
    const protocolServices = services.filter((s) => s.category === "protocol");
    const transportServices = services.filter((s) => s.category === "transport");
    const comboServices = services.filter((s) => s.category === "protocol_transport");
    const dayRentalServices = services.filter((s) => s.category === "day_rental");
    const outstationServices = services.filter((s) => s.category === "outstation");
    // Legacy categories
    const airportServices = services.filter((s) => s.category === "airport");
    const intercityServices = services.filter((s) => s.category === "intercity");

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Transport Services" breadcrumbItems={this.state.breadcrumbItems} />

            <Row className="mb-4">
              <Col>
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0">
                    <i className="ri-settings-3-line mr-2"></i>
                    Service Configuration
                  </h4>
                  <Link to="/transport/requests">
                    <Button color="secondary" outline>
                      <i className="ri-arrow-left-line mr-1"></i> Back to Requests
                    </Button>
                  </Link>
                </div>
              </Col>
            </Row>

            {/* Category Tabs */}
            <Nav tabs className="nav-tabs-custom mb-4">
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "protocol" })}
                  onClick={() => this.toggleTab("protocol")}
                  style={{ cursor: "pointer" }}
                >
                  <i className="ri-user-received-2-line mr-2"></i>
                  Protocol ({protocolServices.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "transport" })}
                  onClick={() => this.toggleTab("transport")}
                  style={{ cursor: "pointer" }}
                >
                  <i className="ri-flight-land-line mr-2"></i>
                  Transport ({transportServices.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "combo" })}
                  onClick={() => this.toggleTab("combo")}
                  style={{ cursor: "pointer" }}
                >
                  <i className="ri-vip-crown-line mr-2"></i>
                  Protocol & Transport ({comboServices.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "day_rental" })}
                  onClick={() => this.toggleTab("day_rental")}
                  style={{ cursor: "pointer" }}
                >
                  <i className="ri-timer-line mr-2"></i>
                  Day Rental ({dayRentalServices.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "outstation" })}
                  onClick={() => this.toggleTab("outstation")}
                  style={{ cursor: "pointer" }}
                >
                  <i className="ri-route-line mr-2"></i>
                  Outstation ({outstationServices.length})
                </NavLink>
              </NavItem>
              {/* Legacy tabs - only show if they have services */}
              {(airportServices.length > 0 || intercityServices.length > 0) && (
                <>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "airport" })}
                      onClick={() => this.toggleTab("airport")}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-flight-takeoff-line mr-2"></i>
                      Legacy Airport ({airportServices.length})
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "intercity" })}
                      onClick={() => this.toggleTab("intercity")}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-taxi-line mr-2"></i>
                      Legacy Intercity ({intercityServices.length})
                    </NavLink>
                  </NavItem>
                </>
              )}
            </Nav>

            <TabContent activeTab={activeTab}>
              {/* Protocol Services */}
              <TabPane tabId="protocol">
                <Row>
                  {protocolServices.map((service) => this.renderServiceCard(service))}
                </Row>
                {protocolServices.length === 0 && (
                  <Card>
                    <CardBody className="text-center py-5">
                      <i className="ri-user-received-2-line ri-3x text-muted mb-3"></i>
                      <h5>No Protocol Services</h5>
                      <p className="text-muted">Run the seed script to add protocol (meet & greet) services.</p>
                      <code>node database/seedTransportServices.js</code>
                    </CardBody>
                  </Card>
                )}
              </TabPane>

              {/* Transport Services */}
              <TabPane tabId="transport">
                <Row>
                  {transportServices.map((service) => this.renderServiceCard(service))}
                </Row>
                {transportServices.length === 0 && (
                  <Card>
                    <CardBody className="text-center py-5">
                      <i className="ri-flight-land-line ri-3x text-muted mb-3"></i>
                      <h5>No Transport Services</h5>
                      <p className="text-muted">Run the seed script to add transport (airport pickup/drop) services.</p>
                      <code>node database/seedTransportServices.js</code>
                    </CardBody>
                  </Card>
                )}
              </TabPane>

              {/* Protocol & Transport Combo */}
              <TabPane tabId="combo">
                <Row>
                  {comboServices.map((service) => this.renderServiceCard(service))}
                </Row>
                {comboServices.length === 0 && (
                  <Card>
                    <CardBody className="text-center py-5">
                      <i className="ri-vip-crown-line ri-3x text-muted mb-3"></i>
                      <h5>No Protocol & Transport Combo Services</h5>
                      <p className="text-muted">Run the seed script to add combo services.</p>
                      <code>node database/seedTransportServices.js</code>
                    </CardBody>
                  </Card>
                )}
              </TabPane>

              {/* Day Rental */}
              <TabPane tabId="day_rental">
                <Row>
                  {dayRentalServices.map((service) => this.renderServiceCard(service))}
                </Row>
                {dayRentalServices.length === 0 && (
                  <Card>
                    <CardBody className="text-center py-5">
                      <i className="ri-timer-line ri-3x text-muted mb-3"></i>
                      <h5>No Day Rental Services</h5>
                      <p className="text-muted">Run the seed script to add hourly rental services.</p>
                      <code>node database/seedTransportServices.js</code>
                    </CardBody>
                  </Card>
                )}
              </TabPane>

              {/* Outstation */}
              <TabPane tabId="outstation">
                <Row>
                  {outstationServices.map((service) => this.renderServiceCard(service))}
                </Row>
                {outstationServices.length === 0 && (
                  <Card>
                    <CardBody className="text-center py-5">
                      <i className="ri-route-line ri-3x text-muted mb-3"></i>
                      <h5>No Outstation Services</h5>
                      <p className="text-muted">Run the seed script to add outstation services.</p>
                      <code>node database/seedTransportServices.js</code>
                    </CardBody>
                  </Card>
                )}
              </TabPane>

              {/* Legacy Airport */}
              <TabPane tabId="airport">
                <Row>
                  {airportServices.map((service) => this.renderServiceCard(service))}
                </Row>
                {airportServices.length === 0 && (
                  <Card>
                    <CardBody className="text-center py-5">
                      <i className="ri-flight-takeoff-line ri-3x text-muted mb-3"></i>
                      <h5>No Legacy Airport Services</h5>
                      <p className="text-muted">These are legacy services for backward compatibility.</p>
                    </CardBody>
                  </Card>
                )}
              </TabPane>

              {/* Legacy Intercity */}
              <TabPane tabId="intercity">
                <Row>
                  {intercityServices.map((service) => this.renderServiceCard(service))}
                </Row>
                {intercityServices.length === 0 && (
                  <Card>
                    <CardBody className="text-center py-5">
                      <i className="ri-taxi-line ri-3x text-muted mb-3"></i>
                      <h5>No Legacy Intercity Services</h5>
                      <p className="text-muted">These are legacy services for backward compatibility.</p>
                    </CardBody>
                  </Card>
                )}
              </TabPane>
            </TabContent>

            {services.length === 0 && (
              <Card>
                <CardBody className="text-center py-5">
                  <i className="ri-car-line ri-3x text-muted mb-3"></i>
                  <h5>No Services Found</h5>
                  <p className="text-muted">Run the seed script to add default services.</p>
                  <code>node database/seedTransportServices.js</code>
                </CardBody>
              </Card>
            )}
          </Container>
        </div>

        {/* Edit Modal */}
        <Modal isOpen={showEditModal} toggle={() => this.setState({ showEditModal: false })} size="lg">
          <ModalHeader toggle={() => this.setState({ showEditModal: false })}>
            Edit Service: {editingService?.serviceName}
          </ModalHeader>
          <ModalBody>
            {editingService && (
              <Row>
                {/* Basic Info */}
                <Col md={6}>
                  <FormGroup>
                    <Label>Base Price / Min Charge ($)</Label>
                    <Input
                      type="number"
                      value={editingService.pricingMode === "fixed" ? editingService.basePrice : editingService.minimumCharge}
                      onChange={(e) =>
                        editingService.pricingMode === "fixed"
                          ? this.updateEditingService("basePrice", parseFloat(e.target.value) || 0)
                          : this.updateEditingService("minimumCharge", parseFloat(e.target.value) || 0)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Estimated Duration (minutes)</Label>
                    <Input
                      type="number"
                      value={editingService.estimatedDuration}
                      onChange={(e) => this.updateEditingService("estimatedDuration", parseInt(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>

                {editingService.pricingMode !== "fixed" && (
                  <Col md={6}>
                    <FormGroup>
                      <Label>Minimum KM</Label>
                      <Input
                        type="number"
                        value={editingService.minimumKm}
                        onChange={(e) => this.updateEditingService("minimumKm", parseInt(e.target.value) || 0)}
                      />
                    </FormGroup>
                  </Col>
                )}

                <Col md={editingService.pricingMode !== "fixed" ? 6 : 6}>
                  <FormGroup>
                    <Label>Max Passengers</Label>
                    <Input
                      type="number"
                      value={editingService.maxPassengers}
                      onChange={(e) => this.updateEditingService("maxPassengers", parseInt(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Max Luggage</Label>
                    <Input
                      type="number"
                      value={editingService.maxLuggage}
                      onChange={(e) => this.updateEditingService("maxLuggage", parseInt(e.target.value) || 0)}
                    />
                  </FormGroup>
                </Col>
                <Col xs={12}>
                  <FormGroup>
                    <Label>Description</Label>
                    <Input
                      type="textarea"
                      rows={2}
                      value={editingService.description}
                      onChange={(e) => this.updateEditingService("description", e.target.value)}
                    />
                  </FormGroup>
                </Col>

                {/* Dynamic Tiered Pricing (Protocol, Transport, Combo) */}
                {editingService.pricingMode === "tiered" && (
                  <>
                    <Col xs={12}>
                      <hr />
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">
                          <i className="ri-price-tag-3-line mr-2"></i>
                          Tier Pricing
                        </h6>
                        <Link to="/transport/service-tiers" className="btn btn-sm btn-outline-primary">
                          <i className="ri-settings-3-line mr-1"></i>
                          Manage Tiers
                        </Link>
                      </div>
                      <small className="text-muted d-block mb-3">
                        Set day and night prices for each tier.
                      </small>
                    </Col>

                    {/* Dynamic tier pricing - renders all linked service tiers */}
                    {(editingService.tierPricing || []).map((tier) => (
                      <Col md={6} lg={4} key={tier.tierCode}>
                        <div
                          className="p-3 border rounded mb-3"
                          style={{ borderLeft: `4px solid ${tier.tierColor || '#5664d2'}` }}
                        >
                          <div className="d-flex align-items-center mb-2">
                            <i className={`${tier.tierIcon || 'ri-car-line'} mr-2`} style={{ color: tier.tierColor }}></i>
                            <strong>{tier.tierName || tier.tierCode}</strong>
                          </div>
                          <FormGroup className="mb-2">
                            <Label className="small mb-1">
                              <i className="ri-sun-line mr-1 text-warning"></i>
                              Day Price ($)
                            </Label>
                            <Input
                              type="number"
                              bsSize="sm"
                              value={tier.dayPrice || 0}
                              onChange={(e) => this.updateDynamicTierPricing(tier.tierCode, "dayPrice", parseFloat(e.target.value) || 0)}
                            />
                          </FormGroup>
                          <FormGroup className="mb-0">
                            <Label className="small mb-1">
                              <i className="ri-moon-line mr-1 text-info"></i>
                              Night Price ($)
                            </Label>
                            <Input
                              type="number"
                              bsSize="sm"
                              value={tier.nightPrice || 0}
                              onChange={(e) => this.updateDynamicTierPricing(tier.tierCode, "nightPrice", parseFloat(e.target.value) || 0)}
                            />
                          </FormGroup>
                        </div>
                      </Col>
                    ))}

                    {(!editingService.tierPricing || editingService.tierPricing.length === 0) && (
                      <Col xs={12}>
                        <div className="alert alert-warning">
                          <i className="ri-information-line mr-2"></i>
                          No service tiers found. <Link to="/transport/service-tiers">Add service tiers</Link> first.
                        </div>
                      </Col>
                    )}

                    <Col xs={12}>
                      <small className="text-info">
                        <i className="ri-information-line mr-1"></i>
                        Tiers are managed in <Link to="/transport/service-tiers">Service Tiers</Link>. Add new tiers there and they will appear here automatically.
                      </small>
                    </Col>
                  </>
                )}

                {/* Hourly Pricing (Day Rental) */}
                {editingService.pricingMode === "hourly" && (
                  <>
                    <Col xs={12}>
                      <hr />
                      <h6>
                        <i className="ri-timer-line mr-2"></i>
                        Hourly Pricing
                      </h6>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Minimum Hours</Label>
                        <Input
                          type="number"
                          min="1"
                          value={editingService.hourlyPricing?.minimumHours || 4}
                          onChange={(e) => this.updateHourlyPricing("minimumHours", parseInt(e.target.value) || 4)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Default Rate Per Hour ($)</Label>
                        <Input
                          type="number"
                          value={editingService.hourlyPricing?.ratePerHour || 0}
                          onChange={(e) => this.updateHourlyPricing("ratePerHour", parseFloat(e.target.value) || 0)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Standard Rate ($/hr)</Label>
                        <Input
                          type="number"
                          value={editingService.hourlyPricing?.standardRate || 0}
                          onChange={(e) => this.updateHourlyPricing("standardRate", parseFloat(e.target.value) || 0)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Comfort Rate ($/hr)</Label>
                        <Input
                          type="number"
                          value={editingService.hourlyPricing?.comfortRate || 0}
                          onChange={(e) => this.updateHourlyPricing("comfortRate", parseFloat(e.target.value) || 0)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>VIP Rate ($/hr)</Label>
                        <Input
                          type="number"
                          value={editingService.hourlyPricing?.vipRate || 0}
                          onChange={(e) => this.updateHourlyPricing("vipRate", parseFloat(e.target.value) || 0)}
                        />
                      </FormGroup>
                    </Col>
                  </>
                )}

                {/* Outstation Rates (only for day_night_km services) */}
                {editingService.pricingMode === "day_night_km" && (
                  <>
                    <Col xs={12}>
                      <hr />
                      <h6>
                        <i className="ri-moon-line mr-2"></i>
                        Outstation Rates
                      </h6>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Day Rate Multiplier</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={editingService.outstationRates?.dayRateMultiplier || 1.0}
                          onChange={(e) => this.updateOutstationRates("dayRateMultiplier", parseFloat(e.target.value) || 1.0)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Night Rate Multiplier</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={editingService.outstationRates?.nightRateMultiplier || 1.5}
                          onChange={(e) => this.updateOutstationRates("nightRateMultiplier", parseFloat(e.target.value) || 1.5)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Night Start Hour</Label>
                        <Input
                          type="number"
                          min="0"
                          max="23"
                          value={editingService.outstationRates?.nightStartHour || 20}
                          onChange={(e) => this.updateOutstationRates("nightStartHour", parseInt(e.target.value) || 20)}
                        />
                        <small className="text-muted">0-23 (e.g., 20 = 8 PM)</small>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Night End Hour</Label>
                        <Input
                          type="number"
                          min="0"
                          max="23"
                          value={editingService.outstationRates?.nightEndHour || 6}
                          onChange={(e) => this.updateOutstationRates("nightEndHour", parseInt(e.target.value) || 6)}
                        />
                        <small className="text-muted">0-23 (e.g., 6 = 6 AM)</small>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Driver Allowance Per Night ($)</Label>
                        <Input
                          type="number"
                          value={editingService.outstationRates?.driverAllowancePerNight || 50}
                          onChange={(e) => this.updateOutstationRates("driverAllowancePerNight", parseFloat(e.target.value) || 50)}
                        />
                      </FormGroup>
                    </Col>
                  </>
                )}

                {/* Discount Settings */}
                <Col xs={12}>
                  <hr />
                  <h6>
                    <i className="ri-percent-line mr-2"></i>
                    Discount Settings
                  </h6>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Enable Discount</Label>
                    <div>
                      <CustomInput
                        type="switch"
                        id="discountEnabled"
                        checked={editingService.discount?.isEnabled || false}
                        onChange={(e) => this.updateDiscount("isEnabled", e.target.checked)}
                      />
                    </div>
                  </FormGroup>
                </Col>
                {editingService.discount?.isEnabled && (
                  <>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Type</Label>
                        <Input
                          type="select"
                          value={editingService.discount?.type || "percentage"}
                          onChange={(e) => this.updateDiscount("type", e.target.value)}
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount ($)</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Value</Label>
                        <Input
                          type="number"
                          value={editingService.discount?.value || 0}
                          onChange={(e) => this.updateDiscount("value", parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Display Label</Label>
                        <Input
                          type="text"
                          value={editingService.discount?.displayLabel || ""}
                          onChange={(e) => this.updateDiscount("displayLabel", e.target.value)}
                          placeholder="e.g., 10% OFF"
                        />
                      </FormGroup>
                    </Col>
                  </>
                )}

                <Col xs={12} className="mt-3">
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={editingService.isActive}
                        onChange={(e) => this.updateEditingService("isActive", e.target.checked)}
                      />
                      {" "}Service is Active
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => this.setState({ showEditModal: false })}>
              Cancel
            </Button>
            <Button color="primary" onClick={this.handleSave}>
              Save Changes
            </Button>
          </ModalFooter>
        </Modal>

        <ToastContainer />
      </React.Fragment>
    );
  }
}

export default TransportServices;
